import {
  CompanyLogoContentType,
  type CompanyLogoCreateFileDto,
  type CompanyLogoSelectDto,
  type CompanyLogoUploadSelectDto,
} from '@@types/api.types';
import {
  ALLOWED_COMPANY_LOGO_CONTENT_TYPE_SET,
  MAX_COMPANY_LOGO_SIZE_BYTES,
  MAX_COMPANY_LOGO_SIZE_ERROR_MESSAGE,
} from '@azymmetric/shared/attachments/attachment.constants';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './keys';
import { useGetCurrentUser, useGetUserSelectedCompanyId } from './users.query';

export const INVALID_COMPANY_LOGO_FORMAT_ERROR_MESSAGE = 'Invalid image format';

type InnovatorCompleteCompanyLogoUploadParams = {
  attachmentId: string;
};

export const resolveCompanyLogoContentType = (contentType?: string): CompanyLogoContentType | undefined => {
  switch (contentType) {
    case CompanyLogoContentType.ImageJpeg:
      return CompanyLogoContentType.ImageJpeg;
    case CompanyLogoContentType.ImagePng:
      return CompanyLogoContentType.ImagePng;
    case CompanyLogoContentType.ImageSvgXml:
      return CompanyLogoContentType.ImageSvgXml;
    case CompanyLogoContentType.ImageWebp:
      return CompanyLogoContentType.ImageWebp;
    default:
      return undefined;
  }
};

export const validateCompanyLogoFile = (file: File): string | undefined => {
  if (!ALLOWED_COMPANY_LOGO_CONTENT_TYPE_SET.has(file.type)) {
    return INVALID_COMPANY_LOGO_FORMAT_ERROR_MESSAGE;
  }

  if (file.size > MAX_COMPANY_LOGO_SIZE_BYTES) {
    return MAX_COMPANY_LOGO_SIZE_ERROR_MESSAGE;
  }

  return undefined;
};

const invalidateCompanyQueries = (params: {
  companyId: string | undefined;
  queryClient: ReturnType<typeof useQueryClient>;
}): void => {
  const { companyId, queryClient } = params;

  queryClient.invalidateQueries({
    queryKey: queryKeys.adminCompanies.root,
  });
  if (companyId) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.companyProfile.getCompanyProfile(companyId),
    });
  }
  queryClient.invalidateQueries({
    queryKey: queryKeys.companyProfile.getCompanyProfiles,
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.intelOrganization.root,
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.parentOrganizations.getProjects,
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.users.getCurrentUser,
  });
};

export const putCompanyLogoFileToS3 = async (params: {
  contentType: string;
  file: File;
  uploadUrl: string;
}): Promise<void> => {
  const { contentType, file, uploadUrl } = params;

  const s3Response = await window.fetch(uploadUrl, {
    body: file,
    headers: { 'Content-Type': contentType },
    method: 'PUT',
  });

  if (!s3Response.ok) {
    throw new Error(`Logo upload failed: ${s3Response.status} ${s3Response.statusText}`);
  }
};

export const useInnovatorCompleteCompanyLogoUpload = () => {
  const { apiPost } = useApi();
  const { isInnovator, isIntel } = useGetCurrentUser();
  const queryClient = useQueryClient();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  return useMutation<CompanyLogoSelectDto, ApiErrorWrapper, InnovatorCompleteCompanyLogoUploadParams>({
    mutationFn: async (data) => {
      if (!isInnovator && !isIntel) {
        throw new Error('Only organization users can manage company logos.');
      }

      const { attachmentId } = data;
      const path = isIntel
        ? `/v1/intel/organization/logo/${attachmentId}/complete`
        : `/v1/innovator/companies/logo/${attachmentId}/complete`;

      return await apiPost<CompanyLogoSelectDto>({
        path,
      });
    },
    onSuccess: async () => {
      invalidateCompanyQueries({
        companyId: selectedCompanyId,
        queryClient,
      });
    },
  });
};

export const useInnovatorDeleteCompanyLogo = () => {
  const { apiDelete } = useApi();
  const { isInnovator, isIntel } = useGetCurrentUser();
  const queryClient = useQueryClient();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  return useMutation<void, ApiErrorWrapper, void>({
    mutationFn: async () => {
      if (!isInnovator && !isIntel) {
        throw new Error('Only organization users can manage company logos.');
      }

      await apiDelete({
        path: isIntel ? '/v1/intel/organization/logo' : '/v1/innovator/companies/logo',
      });
    },
    onSuccess: async () => {
      invalidateCompanyQueries({
        companyId: selectedCompanyId,
        queryClient,
      });
    },
  });
};

export const useInnovatorRequestCompanyLogoUploadUrl = () => {
  const { apiPost } = useApi();
  const { isInnovator, isIntel } = useGetCurrentUser();

  return useMutation<CompanyLogoUploadSelectDto, ApiErrorWrapper, CompanyLogoCreateFileDto>({
    mutationFn: async (data) => {
      if (!isInnovator && !isIntel) {
        throw new Error('Only organization users can manage company logos.');
      }

      return await apiPost<CompanyLogoUploadSelectDto>({
        body: data,
        path: isIntel ? '/v1/intel/organization/logo/file' : '/v1/innovator/companies/logo/file',
      });
    },
  });
};
